import type { ReactNode } from 'react';
import { EmptyState, Spinner } from './Card';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** 固定列宽，例如 `120px`、`18%` */
  width?: string;
  align?: 'left' | 'right' | 'center';
  /** 移动端隐藏（表格在小屏下横向滚动，次要列可以省掉） */
  hideOnMobile?: boolean;
  title?: string;
}

export interface DataTableProps<T> {
  rows: readonly T[];
  columns: readonly Column<T>[];
  getRowId: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  onRowClick?: (row: T) => void;
  selectedIds?: ReadonlySet<string>;
  onSelectedChange?: (next: Set<string>) => void;
  /** 每行左侧额外内容（例如拖拽手柄、状态点） */
  leading?: (row: T) => ReactNode;
  rowTone?: (row: T) => 'default' | 'warning' | 'critical';
  dense?: boolean;
  maxHeight?: string;
}

const ROW_TONES = {
  default: '',
  warning: 'bg-warning-soft/40',
  critical: 'bg-critical-soft/50',
} as const;

/**
 * 通用数据表。
 *
 * 小屏下整体横向滚动而不是压缩列宽 —— 无人机运维表格里列语义很强，
 * 挤在一起反而不可读。
 */
export function DataTable<T>({
  rows,
  columns,
  getRowId,
  loading = false,
  empty,
  emptyTitle = '暂无数据',
  emptyDescription,
  onRowClick,
  selectedIds,
  onSelectedChange,
  leading,
  rowTone,
  dense = false,
  maxHeight,
}: DataTableProps<T>) {
  const selectable = Boolean(selectedIds && onSelectedChange);
  const allSelected = selectable && rows.length > 0 && rows.every((row) => selectedIds!.has(getRowId(row)));
  const someSelected = selectable && !allSelected && rows.some((row) => selectedIds!.has(getRowId(row)));

  const toggleAll = () => {
    if (!selectable) return;
    const next = new Set(selectedIds);
    if (allSelected) {
      for (const row of rows) next.delete(getRowId(row));
    } else {
      for (const row of rows) next.add(getRowId(row));
    }
    onSelectedChange!(next);
  };

  const toggleOne = (id: string) => {
    if (!selectable) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange!(next);
  };

  const cellPad = dense ? 'px-2.5 py-1.5' : 'px-3 py-2.5';

  return (
    <div className="relative overflow-hidden rounded-lg border border-line bg-surface-1">
      <div className="overflow-auto" style={maxHeight ? { maxHeight } : undefined}>
        <table className="w-full min-w-max border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-surface-2">
            <tr className="border-b border-line">
              {selectable ? (
                <th className={['w-9 text-left', cellPad].join(' ')}>
                  <input
                    type="checkbox"
                    aria-label="全选"
                    checked={allSelected}
                    ref={(node) => {
                      if (node) node.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                    className="size-3.5 cursor-pointer accent-[var(--color-accent)]"
                  />
                </th>
              ) : null}
              {leading ? <th className={['w-6', cellPad].join(' ')} /> : null}
              {columns.map((column) => (
                <th
                  key={column.key}
                  title={column.title}
                  style={column.width ? { width: column.width } : undefined}
                  className={[
                    'text-xs font-medium whitespace-nowrap text-fg-muted',
                    cellPad,
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                        ? 'text-center'
                        : 'text-left',
                    column.hideOnMobile ? 'hidden md:table-cell' : '',
                  ].join(' ')}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id = getRowId(row);
              const selected = selectable && selectedIds!.has(id);
              return (
                <tr
                  key={id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={[
                    'border-b border-line/70 last:border-b-0',
                    selected ? 'bg-accent-soft' : ROW_TONES[rowTone?.(row) ?? 'default'],
                    onRowClick ? 'cursor-pointer hover:bg-surface-2' : '',
                  ].join(' ')}
                >
                  {selectable ? (
                    <td className={cellPad} onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`选择 ${id}`}
                        checked={selected}
                        onChange={() => toggleOne(id)}
                        className="size-3.5 cursor-pointer accent-[var(--color-accent)]"
                      />
                    </td>
                  ) : null}
                  {leading ? <td className={cellPad}>{leading(row)}</td> : null}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={[
                        cellPad,
                        column.align === 'right'
                          ? 'text-right'
                          : column.align === 'center'
                            ? 'text-center'
                            : 'text-left',
                        column.hideOnMobile ? 'hidden md:table-cell' : '',
                      ].join(' ')}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {!loading && rows.length === 0 ? (
        empty ?? <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : null}
    </div>
  );
}
